/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  barcode_id: '',
  supplier_id: '',
  created_at: '',
  cost_sell: '',
  cost: '',
};

function ProductSupplier({ parent, reload }) {
  const mod = 'product_supplier';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cboSupplier, setCboSupplier] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [notify, setNotify] = useState(null);
  const [field, setField] = useState({});
  const [editSupplier, setEditSupplier] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = () => {
    setLoading(true);
    FetchData('POST', `/${mod}`, app.token, {
      id: parent.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setLoading(false);

        setList(result.list);
        setCboSupplier(result.supplier);
        setField(result.parent);
      }
    });
  };

  const open = () => {
    setAttr(initAttr);
    setModalOpen(true);
    setEditSupplier(false);

    view();
  };

  const addConfirm = () => {
    if (supplier === null) return;
    if (!Func.verifyLength(attr.cost, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    if (supplier.id === 'ALL') setEditSupplier(true);

    setConfirm(false);
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      barcode_id: parent.id,
      supplier_id: supplier.id,
      cost: attr.cost,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setSupplier(null);
        setAttr(initAttr);
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <MDBox mb={0.5}>
              <InputLabel>ผู้จัดจำหน่าย</InputLabel>
            </MDBox>
            <Autocomplete
              options={cboSupplier}
              isOptionEqualToValue={(option, v) => option.id === v.id}
              getOptionLabel={(option) => option.name}
              onChange={(e, v) => {
                e.preventDefault();
                return setSupplier(v);
              }}
              value={supplier || null}
              noOptionsText='-'
              renderOption={(props, option) => {
                return (
                  <MDBox component='li' {...props} key={option.id}>
                    {option.name}
                  </MDBox>
                );
              }}
              renderInput={(params) => (
                <MDInput
                  variant='standard'
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <MDInput
              label='ราคา'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, cost: e.target.value })}
              value={attr.cost}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
        </Grid>
        <MDBox mt={2}>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={12} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='info' onClick={addConfirm}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </Grid>
            </Grid>
          )}
        </MDBox>
      </MDBox>
    );
  };

  const btnEditMain = () => {
    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton
          variant='text'
          color='success'
          size='small'
          iconOnly
          onClick={() => {
            setSupplier({ id: 'ALL', name: 'ราคาหลัก' });
            setAttr({ ...initAttr, cost: field.cost });
          }}
        >
          <Icon>border_color</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnEdit = (item) => {
    if (item.po_id > 0) return null;

    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton
          variant='text'
          color='success'
          size='small'
          iconOnly
          onClick={() => {
            setSupplier({ id: item.supplier_id, name: item.supplier_name });
            setAttr({ ...initAttr, cost: item.cost });
          }}
        >
          <Icon>border_color</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    if (!app.perm.del) return null;
    if (item.po_id > 0)
      return (
        <Tooltip title='สร้างจากการสั่งซื้อ'>
          <MDButton variant='text' color='dark' size='small' iconOnly>
            <Icon>add_task</Icon>
          </MDButton>
        </Tooltip>
      );

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderList = () => {
    return (
      <MDBox className='card'>
        <TableContainer sx={{ boxShadow: 0, minHeight: 300, maxHeight: 300 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>No.</TableCell>
                <TableCell>ผู้จัดจำหน่าย</TableCell>
                <TableCell align='right'>ราคา</TableCell>
                <TableCell align='center'>วันที่</TableCell>
                <TableCell width={10}>&nbsp;</TableCell>
                <TableCell width={10}>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow hover>
                <TableCell align='center' width='50'>
                  1
                </TableCell>
                <TableCell align='left'>ราคาหลัก</TableCell>
                <TableCell align='right'>
                  <b>{field.cost}</b>
                </TableCell>
                <TableCell align='center'>-</TableCell>
                <TableCell align='center'>{btnEditMain()}</TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
              {list.map((row, i) => (
                <TableRow key={row.id} hover>
                  <TableCell align='center'>{i + 2}</TableCell>
                  <TableCell align='left'>{row.supplier_name}</TableCell>
                  <TableCell align='right'>{row.cost}</TableCell>
                  <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                  <TableCell align='center'>{btnEdit(row)}</TableCell>
                  <TableCell align='center'>{btnDel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const btnOpen = () => {
    return (
      <Tooltip title='ราคาซื้อ'>
        <MDButton
          variant='text'
          color='error'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'inline-block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={open}
        >
          {Func.numberDisplay(parent.cost, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='lg'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => {
          setModalOpen(false);
          if (editSupplier) reload();
        }}
      >
        <DialogTitle>
          <MDBox className='cut' sx={{ width: 400 }}>
            ราคาซื้อ : {parent.code}
          </MDBox>
          <IconButton
            aria-label='close'
            onClick={() => {
              setModalOpen(false);
              if (editSupplier) reload();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderInfo()}
          {renderList()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}

      {renderLoading()}
      {notify}
    </div>
  );
}

ProductSupplier.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    barcode: PropTypes.string.isRequired,
    cost: PropTypes.string.isRequired,
    cost_sell: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  reload: PropTypes.func.isRequired,
};

export default ProductSupplier;
